import React from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import type { L2CComparisonMode, L2CDriver, L2CMetric, L2CMover, L2CPanel, L2CPeriodGrain, L2CStage, L2CStatus, L2CTrendPoint } from '../../../types/leadToCash';
import { fetchLeadToCashMetricTrend } from '../../../services/leadToCashService';
import { formatL2CPercent, formatMetricValue } from '../utils/formatNumbers';
import { StageDetailNarrativeSkeleton, BreakdownTableSkeleton } from './LeadToCashLoadingSkeleton';
import StageBreakdownTable from './StageBreakdownTable';
import { useCurrency } from '../../../ui/BootstrapProvider';

export { formatMetricValue };

export const STATUS_COLORS: Record<L2CStatus, string> = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
};

// Metrics computed as an AVG(...)/weighted ratio per dimension — breakdown rows
// are independent averages that won't sum (or roll up) to the headline value.
const AVERAGE_METRIC_KEYS = new Set(['avg_deal_score', 'avg_premium', 'cmql']);

const STATUS_LABELS: Record<L2CStatus, string> = {
  green: 'Normal',
  yellow: 'Watch',
  red: 'Notable',
};

const STAGE_SHORT_NAMES: Record<string, string> = {
  spend: 'Spend',
  mql: 'MQL',
  connect: 'Connect',
  cas: 'CAS',
  approved: 'Approved',
  placement: 'Placement',
  inforce: 'In-force',
  cash: 'Cash',
  persistency: 'Persistency',
  // legacy ids (cached sessions)
  pre_mql: 'Spend',
  gross_leads: 'MQL',
  cmql: 'cMQL',
  call_connect: 'Connect',
  sqo: 'CAS',
  decision: 'Approved',
  commission: 'Cash',
};

export function getStageDisplayName(stage: Pick<L2CStage, 'id' | 'name'>): string {
  return STAGE_SHORT_NAMES[stage.id] ?? stage.name;
}

function DeltaIcon({ direction }: { direction: L2CMetric['direction'] }) {
  if (direction === 'up') return <ArrowUpwardRounded sx={{ fontSize: 12 }} />;
  if (direction === 'down') return <ArrowDownwardRounded sx={{ fontSize: 12 }} />;
  return <RemoveRounded sx={{ fontSize: 12 }} />;
}

function sentimentColor(metric: L2CMetric): string {
  if (metric.sentiment === 'bad') return 'error.main';
  if (metric.sentiment === 'good') return 'success.main';
  return 'text.secondary';
}

function priorPeriodLabel(grain: L2CPeriodGrain): string {
  if (grain === 'year') return 'Prior year';
  if (grain === 'month') return 'Prior month';
  if (grain === 'day') return 'Prior day';
  return 'Prior week';
}

/**
 * Compact date-range label for breakdown table column headers, e.g. "Jul 7–13" or
 * "Jun 30 – Jul 6". Dates are unambiguous regardless of the selected grain/span, unlike
 * a fixed "This wk"/"This period" label which can mislabel arbitrary custom ranges.
 */
function formatShortPeriodRange(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const fmt = (d: Date, withYear: boolean) =>
    d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: withYear ? 'numeric' : undefined,
      timeZone: 'UTC',
    });

  if (start === end) return fmt(startDate, !sameYear);
  if (sameYear && startDate.getUTCMonth() === endDate.getUTCMonth()) {
    const month = startDate.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    return `${month} ${startDate.getUTCDate()}–${endDate.getUTCDate()}`;
  }
  return `${fmt(startDate, !sameYear)} – ${fmt(endDate, !sameYear)}`;
}

function formatPriorValue(metric: L2CMetric, currencySymbol = '$'): string {
  if (metric.prior_value == null) return '—';
  return formatMetricValue({ value: metric.prior_value, format: metric.format }, currencySymbol);
}

interface StageRowProps {
  stage: L2CStage;
  selected: boolean;
  onClick: () => void;
  comparisonLabel: string;
}

interface PhaseDef {
  id: string;
  label: string;
}

/** Chain stages grouped into lifecycle phases (Pre MQL → Realize). */
const CHAIN_PHASES: PhaseDef[] = [
  { id: 'pre_mql', label: 'Pre MQL' },
  { id: 'mql', label: 'MQL' },
  { id: 'post_mql', label: 'Post MQL' },
  { id: 'fulfill', label: 'Fulfill' },
  { id: 'realize', label: 'Realize' },
];

const STAGE_PHASE_MAP: Record<string, string> = {
  spend: 'pre_mql',
  mql: 'mql',
  cmql: 'mql',
  connect: 'post_mql',
  cas: 'post_mql',
  approved: 'fulfill',
  placement: 'fulfill',
  inforce: 'fulfill',
  cash: 'realize',
  persistency: 'realize',
  pre_mql: 'pre_mql',
  gross_leads: 'mql',
  call_connect: 'post_mql',
  sqo: 'post_mql',
  decision: 'fulfill',
  commission: 'realize',
};

function worstStageStatus(stages: L2CStage[]): L2CStatus {
  if (stages.some((s) => s.status === 'red')) return 'red';
  if (stages.some((s) => s.status === 'yellow')) return 'yellow';
  return 'green';
}

/**
 * The L2C period picker always sends an explicit range, so the backend labels
 * every comparison "Custom". In the chain UI we render that as "vs prior" so
 * deltas read meaningfully (e.g. "↑ vs prior 75.9%").
 */
function displayComparisonLabel(label?: string | null): string | undefined {
  if (label == null) return undefined;
  if (label === 'Custom' || label === 'Custom range') return 'vs prior';
  return label;
}

function getPrimaryMetric(stage: Pick<L2CStage, 'headline_metrics' | 'primary_metric_key'>): L2CMetric | undefined {
  if (stage.primary_metric_key) {
    return (
      stage.headline_metrics.find((m) => m.key === stage.primary_metric_key)
      ?? stage.headline_metrics[0]
    );
  }
  return stage.headline_metrics[0];
}

function getCardMetrics(stage: L2CStage): L2CMetric[] {
  const keys = stage.card_metric_keys?.length
    ? stage.card_metric_keys
    : stage.primary_metric_key
      ? [stage.primary_metric_key]
      : stage.headline_metrics.slice(0, 1).map((m) => m.key);
  const allMetrics = [...stage.headline_metrics, ...(stage.supporting_metrics ?? [])];
  return keys
    .map((key) => allMetrics.find((m) => m.key === key))
    .filter((m): m is L2CMetric => Boolean(m));
}

function PhaseStageRow({ stage, selected, onClick, comparisonLabel }: StageRowProps) {
  const { symbol: currencySymbol } = useCurrency();
  const color = STATUS_COLORS[stage.status];
  const shortName = getStageDisplayName(stage);
  const metric = getCardMetrics(stage)[0];

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-selected={selected}
      aria-current={selected ? 'step' : undefined}
      aria-label={metric ? `${shortName}, ${metric.label} ${formatMetricValue(metric, currencySymbol)}` : shortName}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={(t) => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.75,
        minWidth: 0,
        cursor: 'pointer',
        borderRadius: `${t.ds?.radius?.sm ?? 6}px`,
        border: '1px solid',
        borderColor: selected ? t.palette.primary.main : 'transparent',
        bgcolor: selected ? alpha(t.palette.primary.main, 0.1) : 'transparent',
        transition: `background-color ${t.ds?.motion?.duration?.fast ?? 150}ms, border-color ${t.ds?.motion?.duration?.fast ?? 150}ms`,
        '&:hover': {
          bgcolor: selected ? alpha(t.palette.primary.main, 0.14) : t.palette.action.hover,
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: t.tokens.shadow.focusRing,
        },
      })}
    >
      <Tooltip title={STATUS_LABELS[stage.status]} arrow>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      </Tooltip>
      <Typography
        variant="body2"
        fontWeight={selected ? 700 : 600}
        noWrap
        sx={{ fontSize: '0.8125rem', color: 'text.primary', flex: 1, minWidth: 0 }}
      >
        {shortName}
      </Typography>
      {metric && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title={metric.label} arrow>
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}
            >
              {formatMetricValue(metric, currencySymbol)}
            </Typography>
          </Tooltip>
          {metric.wow_pct != null && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.15}
              title={`${comparisonLabel} change`}
              sx={{ color: sentimentColor(metric) }}
            >
              <DeltaIcon direction={metric.direction} />
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.625rem' }}>
                {formatL2CPercent(metric.wow_pct)}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

interface PhaseCardProps {
  index: number;
  label: string;
  stages: L2CStage[];
  selectedStageId: string | null;
  onSelectStage: (stageId: string) => void;
  comparisonLabel: string;
}

function PhaseCard({ index, label, stages, selectedStageId, onSelectStage, comparisonLabel }: PhaseCardProps) {
  const worst = worstStageStatus(stages);
  const worstColor = STATUS_COLORS[worst];

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      <Box
        sx={(t) => ({
          px: 1.25,
          py: 0.75,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.surface.sunken,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          <Box
            sx={(t) => ({
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: alpha(t.palette.text.primary, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            })}
          >
            <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
              {index}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            noWrap
            sx={{
              fontSize: '0.75rem',
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'text.primary',
            }}
          >
            {label}
          </Typography>
        </Stack>
        <Tooltip title={`Worst in phase: ${STATUS_LABELS[worst]}`} arrow>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: worstColor, flexShrink: 0 }} />
        </Tooltip>
      </Box>

      <Stack spacing={0.25} sx={{ p: 0.5 }}>
        {stages.map((stage) => (
          <PhaseStageRow
            key={stage.id}
            stage={stage}
            selected={selectedStageId === stage.id}
            onClick={() => onSelectStage(stage.id)}
            comparisonLabel={comparisonLabel}
          />
        ))}
      </Stack>
    </Paper>
  );
}

interface ChainViewProps {
  stages: L2CStage[];
  selectedStageId: string | null;
  onSelectStage: (stageId: string) => void;
  comparisonLabel?: string | null;
  periodGrain?: L2CPeriodGrain;
}

export default function ChainView({
  stages,
  selectedStageId,
  onSelectStage,
  comparisonLabel,
}: ChainViewProps) {
  const visible = [...stages]
    .filter((s) => !s.data_unavailable)
    .sort((a, b) => a.order - b.order);

  const cmpLabel = displayComparisonLabel(comparisonLabel) ?? 'WoW';

  const grouped = new Map<string, L2CStage[]>();
  for (const stage of visible) {
    const phase = STAGE_PHASE_MAP[stage.id] ?? 'other';
    const existing = grouped.get(phase);
    if (existing) existing.push(stage);
    else grouped.set(phase, [stage]);
  }

  const orderedGroups = [...CHAIN_PHASES, { id: 'other', label: 'Other' }]
    .map((phase) => ({ ...phase, stages: grouped.get(phase.id) ?? [] }))
    .filter((group) => group.stages.length > 0);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(5, minmax(200px, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
        gap: 1,
        alignItems: 'start',
        overflowX: { xs: 'auto', lg: 'visible' },
        pb: { xs: 0.5, lg: 0 },
      }}
    >
      {orderedGroups.map((group, idx) => (
        <PhaseCard
          key={group.id}
          index={idx + 1}
          label={group.label}
          stages={group.stages}
          selectedStageId={selectedStageId}
          onSelectStage={onSelectStage}
          comparisonLabel={cmpLabel}
        />
      ))}
    </Box>
  );
}

interface StageDetailPanelProps {
  stage: L2CStage | null;
  comparisonMode?: L2CComparisonMode;
  comparisonLabel?: string | null;
  periodGrain?: L2CPeriodGrain;
  /** Actual selected/comparison date bounds — used to label breakdown table columns with real dates. */
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  priorPeriodStart?: string | null;
  priorPeriodEnd?: string | null;
  detailLoading?: boolean;
  narrativeLoading?: boolean;
  onDriverChange?: (driverId: string) => void;
  onOpenChat?: () => void;
  headlineBreakdownDriverId?: string | null;
  activeBreakdownMetricKey?: string | null;
  onSelectBreakdownMetric?: (metricKey: string | null) => void;
  breakdownDriverLoading?: boolean;
  metricBreakdownLoading?: boolean;
  metricBreakdownMovers?: L2CMover[];
  metricBreakdownDrivers?: L2CDriver[];
  metricBreakdownActiveDriver?: string | null;
  metricBreakdownUnavailable?: boolean;
  onMetricDriverChange?: (driverId: string) => void;
  /** Plain-language summary of the currently selected metric card ("Metric Summary"). */
  metricSummary?: string | null;
}

const BREAKDOWN_UNAVAILABLE_MESSAGE =
  'Dimension breakdown could not be loaded right now. Headline metrics and the executive summary are still shown.';

function MetricTrendSparkline({ stageId, metricKey }: { stageId: string; metricKey: string }) {
  const [points, setPoints] = React.useState<L2CTrendPoint[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    void fetchLeadToCashMetricTrend(stageId, metricKey, 12)
      .then((res) => {
        if (!cancelled) setPoints(res.points ?? []);
      })
      .catch(() => {
        if (!cancelled) setPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [stageId, metricKey]);

  if (points.length < 2) return null;

  const values = points.map((p) => (typeof p.value === 'number' ? p.value : 0));
  const max = Math.max(...values, 1);

  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.25} sx={{ height: 18, mt: 0.25 }}>
      {points.map((point, idx) => {
        const v = typeof point.value === 'number' ? point.value : 0;
        const h = Math.max(2, Math.round((v / max) * 16));
        const status: L2CStatus = point.status ?? point.stage_status ?? 'green';
        const color = STATUS_COLORS[status];
        return (
          <Tooltip
            key={`${point.period_start}-${idx}`}
            title={`${point.period_start}: ${v}`}
            arrow
          >
            <Box
              sx={{
                width: 4,
                height: h,
                borderRadius: 0.5,
                bgcolor: alpha(color, 0.85),
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function MetricCard({
  metric,
  stageId,
  isPrimary = false,
  selected = false,
  clickable = false,
  showSparkline = false,
  onClick,
}: {
  metric: L2CMetric;
  stageId?: string;
  isPrimary?: boolean;
  selected?: boolean;
  clickable?: boolean;
  showSparkline?: boolean;
  onClick?: () => void;
}) {
  const { symbol: currencySymbol } = useCurrency();
  const statusColor = STATUS_COLORS[metric.status];
  const maturingLabel = metric.maturation_note
    ? /still open|maturing/i.test(metric.maturation_note)
      ? 'Maturing'
      : 'In progress'
    : null;

  return (
    <Paper
      variant="outlined"
      onClick={clickable ? onClick : undefined}
      sx={(t) => ({
        position: 'relative',
        flex: '0 0 auto',
        width: 208,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: `${t.ds?.radius?.sm ?? 6}px`,
        borderColor: selected ? t.palette.primary.main : t.tokens.border.subtle,
        bgcolor: selected ? alpha(t.palette.primary.main, 0.06) : undefined,
        cursor: clickable ? 'pointer' : 'default',
        transition: `border-color ${t.ds?.motion?.duration?.fast ?? 150}ms, background-color ${t.ds?.motion?.duration?.fast ?? 150}ms`,
        '&:hover': clickable
          ? { borderColor: t.palette.primary.main, bgcolor: alpha(t.palette.primary.main, 0.04) }
          : undefined,
      })}
    >
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: alpha(statusColor, 0.06),
        })}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          {isPrimary && (
            <Tooltip title="Headline metric" arrow>
              <StarRounded sx={{ fontSize: 15, color: 'warning.main', flexShrink: 0 }} />
            </Tooltip>
          )}
          <Tooltip title={metric.label} arrow>
            <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {metric.label}
            </Typography>
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Chip
            size="small"
            color={metric.status === 'red' ? 'error' : metric.status === 'yellow' ? 'warning' : 'success'}
            label={STATUS_LABELS[metric.status]}
            sx={{
              height: 18,
              fontSize: '0.625rem',
              fontWeight: 700,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {(metric.status_reason || metric.definition_note) && (
            <Tooltip
              arrow
              slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}
              title={
                <Stack spacing={metric.status_reason && metric.definition_note ? 1 : 0}>
                  {metric.status_reason && (
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                      {metric.status_reason}
                    </Typography>
                  )}
                  {metric.definition_note && (
                    <Box
                      sx={{
                        pt: metric.status_reason ? 1 : 0,
                        borderTop: metric.status_reason ? '1px solid rgba(255,255,255,0.2)' : 'none',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 700, opacity: 0.75, mb: 0.25 }}
                      >
                        Methodology
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                        {metric.definition_note}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              }
            >
              <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 1.25, py: 1.25, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ rowGap: 0.5 }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            lineHeight={1.1}
            sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          >
            {formatMetricValue(metric, currencySymbol)}
          </Typography>
          {metric.wow_pct != null && (
            <Chip
              size="small"
              color={metric.sentiment === 'bad' ? 'error' : metric.sentiment === 'good' ? 'success' : 'default'}
              icon={<DeltaIcon direction={metric.direction} />}
              label={formatL2CPercent(metric.wow_pct)}
              sx={{
                height: 22,
                fontWeight: 800,
                fontSize: '0.75rem',
                flexShrink: 0,
                '& .MuiChip-icon': { fontSize: 14, ml: 0.5, mr: -0.25 },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            vs{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {metric.prior_value != null
                ? formatMetricValue({ ...metric, value: metric.prior_value }, currencySymbol)
                : '—'}
            </Box>
            {' '}prior
          </Typography>
          {maturingLabel && (
            <Tooltip title={metric.maturation_note} arrow>
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={maturingLabel}
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Tooltip>
          )}
        </Stack>
        {showSparkline && stageId && (
          <MetricTrendSparkline stageId={stageId} metricKey={metric.key} />
        )}
      </Box>
    </Paper>
  );
}

function StageAllMetricsRow({
  stageId,
  headlineMetrics,
  supportingMetrics,
  headlineKeys,
  primaryKey,
  activeKey,
  onSelect,
}: {
  stageId: string;
  headlineMetrics: L2CMetric[];
  supportingMetrics: L2CMetric[];
  headlineKeys: string[];
  primaryKey?: string;
  activeKey?: string | null;
  onSelect?: (metricKey: string | null) => void;
}) {
  const headlineKeySet = new Set(headlineKeys);
  const ordered = [...headlineMetrics, ...supportingMetrics];
  if (!ordered.length) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          gap: 1.25,
        }}
      >
        {ordered.map((m) => {
          const selected = activeKey ? activeKey === m.key : m.key === primaryKey;
          return (
            <MetricCard
              key={m.key}
              metric={m}
              stageId={stageId}
              isPrimary={headlineKeySet.has(m.key)}
              showSparkline={m.key === primaryKey}
              selected={selected}
              clickable={Boolean(onSelect)}
              onClick={onSelect ? () => onSelect(selected ? null : m.key) : undefined}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function StageNarrativeCard({
  metricLabel,
  summary,
  loading = false,
}: {
  metricLabel?: string;
  summary?: string | null;
  loading?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        mb: 3,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        borderColor: alpha(t.palette.info.main, 0.2),
        bgcolor: alpha(t.palette.info.main, 0.03),
        overflow: 'hidden',
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={(t) => ({
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${alpha(t.palette.info.main, 0.12)}`,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <InsightsRounded sx={{ fontSize: 18, color: 'info.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Metric Summary{metricLabel ? ` · ${metricLabel}` : ''}
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ p: 2 }}>
        {loading ? (
          <Stack spacing={0.75}>
            <Skeleton variant="text" width="95%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.primary' }}>
            {summary || 'No summary available for this metric yet.'}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export function StageDetailPanel({
  stage,
  comparisonMode = 'wow',
  comparisonLabel,
  periodGrain = 'week',
  currentPeriodStart = null,
  currentPeriodEnd = null,
  priorPeriodStart = null,
  priorPeriodEnd = null,
  detailLoading = false,
  narrativeLoading = false,
  onDriverChange,
  onOpenChat,
  headlineBreakdownDriverId = null,
  activeBreakdownMetricKey = null,
  onSelectBreakdownMetric,
  breakdownDriverLoading = false,
  metricBreakdownLoading = false,
  metricBreakdownMovers,
  metricBreakdownDrivers,
  metricBreakdownActiveDriver,
  metricBreakdownUnavailable = false,
  onMetricDriverChange,
  metricSummary = null,
}: StageDetailPanelProps) {
  const { symbol: currencySymbol } = useCurrency();
  if (!stage || stage.data_unavailable) {
    return (
      <Paper
        variant="outlined"
        sx={(t) => ({
          p: 4,
          borderRadius: `${t.ds?.radius?.md ?? 8}px`,
          bgcolor: t.tokens.surface.sunken,
          borderStyle: 'dashed',
          textAlign: 'center',
        })}
      >
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {stage?.data_unavailable ? 'Stage data unavailable' : 'Select a stage to drill down'}
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={480} mx="auto">
          {stage?.data_unavailable
            ? 'Warehouse data for this stage could not be loaded. Other stages may still be available.'
            : 'Click any stage in the chain above to view week-over-week movement, root-cause breakdown, and recommended next steps.'}
        </Typography>
      </Paper>
    );
  }

  const cmpLabel = displayComparisonLabel(comparisonLabel) ?? (
    comparisonMode === 'dod' ? 'DoD'
      : comparisonMode === 'mom' ? 'MoM'
        : comparisonMode === 'ytd' ? 'YTD'
          : comparisonMode === 'yoy' ? 'YoY'
            : comparisonMode === 'custom' ? 'Custom'
              : 'WoW'
  );
  const periodDesc =
    comparisonMode === 'dod'
      ? 'Day-over-day vs prior calendar day'
      : comparisonMode === 'mom'
      ? 'Month-over-month vs prior calendar month'
      : comparisonMode === 'ytd'
        ? 'Year-to-date vs the same calendar span one year ago'
        : comparisonMode === 'yoy'
          ? 'Year-over-year vs prior calendar year'
          : comparisonMode === 'custom'
            ? 'Selected date range vs comparison date range'
            : 'Week-over-week vs prior Mon–Sun reporting week';
  const currentPeriodLabel =
    formatShortPeriodRange(currentPeriodStart, currentPeriodEnd)
    ?? (periodGrain === 'day' ? 'This day'
      : periodGrain === 'year' ? 'This year'
        : periodGrain === 'month' ? 'This period'
          : 'This wk');
  const priorPeriodLabel =
    formatShortPeriodRange(priorPeriodStart, priorPeriodEnd)
    ?? (periodGrain === 'day' ? 'Prior day'
      : periodGrain === 'year' ? 'Prior year'
        : periodGrain === 'month' ? 'Prior period'
          : 'Prior wk');

  const panel = stage.panel;
  const statusColor = STATUS_COLORS[stage.status];
  const primaryMetric = getPrimaryMetric(stage);
  const supportingMetrics = stage.supporting_metrics ?? [];
  const usingMetricBreakdown = Boolean(activeBreakdownMetricKey);
  const catalogDrivers = panel?.drivers ?? stage.breakdown_drivers ?? [];
  const drivers = usingMetricBreakdown && metricBreakdownDrivers?.length
    ? metricBreakdownDrivers
    : catalogDrivers;
  const activeDriver = usingMetricBreakdown
    ? (metricBreakdownActiveDriver ?? metricBreakdownDrivers?.find((d) => d.default)?.id ?? drivers.find((d) => d.default)?.id ?? drivers[0]?.id)
    : (headlineBreakdownDriverId ?? panel?.active_driver ?? drivers.find((d) => d.default)?.id ?? drivers[0]?.id);
  const breakdownMovers = usingMetricBreakdown
    ? (metricBreakdownMovers ?? [])
    : (panel?.movers ?? []);
  const showBreakdownSkeleton = detailLoading && !panel && !usingMetricBreakdown;
  const driverLoading = breakdownDriverLoading || metricBreakdownLoading;
  const breakdownUnavailable = usingMetricBreakdown
    ? metricBreakdownUnavailable
    : Boolean(panel?.breakdown_unavailable);
  const breakdownPanel: NonNullable<L2CPanel> = usingMetricBreakdown
    ? {
        what_happened: panel?.what_happened ?? '',
        why: panel?.why ?? '',
        suggestion: panel?.suggestion ?? '',
        method_note: panel?.method_note ?? '',
        movers: breakdownMovers,
        active_driver: activeDriver ?? undefined,
        drivers: drivers.length ? drivers : undefined,
        breakdown_unavailable: breakdownUnavailable,
      }
    : panel
      ? {
          ...panel,
          movers: breakdownMovers,
          active_driver: activeDriver ?? undefined,
          drivers: drivers.length ? drivers : panel.drivers,
          breakdown_unavailable: breakdownUnavailable,
        }
      : {
          what_happened: '',
          why: '',
          suggestion: '',
          method_note: '',
          movers: breakdownMovers,
          active_driver: activeDriver ?? undefined,
          drivers: drivers.length ? drivers : undefined,
          breakdown_unavailable: breakdownUnavailable,
        };

  const breakdownMetric = usingMetricBreakdown && activeBreakdownMetricKey
    ? stage.headline_metrics.find((m) => m.key === activeBreakdownMetricKey)
      ?? supportingMetrics.find((m) => m.key === activeBreakdownMetricKey)
    : primaryMetric;
  const breakdownMetricContext = breakdownMetric
    ? {
        label: breakdownMetric.label,
        formattedValue: breakdownMetric.value != null ? formatMetricValue(breakdownMetric, currencySymbol) : null,
        source: supportingMetrics.some((m) => m.key === breakdownMetric.key)
          ? 'supporting' as const
          : 'headline' as const,
        isAverage: breakdownMetric.format === 'ratio' || AVERAGE_METRIC_KEYS.has(breakdownMetric.key),
        isCurrency: breakdownMetric.format === 'currency',
      }
    : null;

  // "Metric Summary" tracks whichever card is selected above: an explicitly clicked
  // metric fetches its summary alongside the breakdown; the default (primary metric)
  // view reads its summary straight off the cached stage panel.
  const metricSummaryText = usingMetricBreakdown
    ? metricSummary ?? null
    : (panel?.metric_narratives?.[breakdownMetric?.key ?? '']?.summary ?? null);
  // The summary is driver-agnostic — only show a loading state while there's no
  // text to display yet (a brand-new metric card). Switching the breakdown's
  // dimension tab re-fetches movers but never changes this text, so keep
  // showing the existing summary rather than flashing a skeleton over it.
  const metricSummaryLoading = usingMetricBreakdown
    ? (metricBreakdownLoading && !metricSummaryText)
    : (detailLoading && !panel);

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        overflow: 'hidden',
        borderColor: alpha(statusColor, 0.35),
      })}
    >
      <Box
        sx={(t) => ({
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: alpha(statusColor, 0.04),
        })}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 10,
                height: 36,
                borderRadius: 1,
                bgcolor: statusColor,
              }}
            />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {stage.order}. {getStageDisplayName(stage)}
                </Typography>
                <Chip
                  size="small"
                  label={STATUS_LABELS[stage.status]}
                  sx={{
                    bgcolor: alpha(statusColor, 0.12),
                    color: statusColor,
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" mt={0.25}>
                {periodDesc}{cmpLabel === 'vs prior' ? '' : ` (${cmpLabel})`}
              </Typography>
            </Box>
          </Stack>
          {onOpenChat && (
            <Tooltip title="Ask about this stage" arrow>
              <IconButton aria-label="Open stage chat" onClick={onOpenChat} size="small">
                <ChatBubbleOutlineRounded />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <StageAllMetricsRow
          stageId={stage.id}
          headlineMetrics={stage.headline_metrics}
          supportingMetrics={supportingMetrics}
          headlineKeys={stage.headline_metric_keys?.length
            ? stage.headline_metric_keys
            : stage.headline_metrics.map((m) => m.key)}
          primaryKey={primaryMetric?.key}
          activeKey={activeBreakdownMetricKey}
          onSelect={onSelectBreakdownMetric}
        />

        {detailLoading && !panel ? (
          <StageDetailNarrativeSkeleton />
        ) : panel ? (
          <StageNarrativeCard
            metricLabel={breakdownMetric?.label}
            summary={metricSummaryText}
            loading={metricSummaryLoading}
          />
        ) : null}

        {panel?.breakdown_unavailable && !usingMetricBreakdown && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {BREAKDOWN_UNAVAILABLE_MESSAGE}
          </Alert>
        )}

        {usingMetricBreakdown && breakdownUnavailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {BREAKDOWN_UNAVAILABLE_MESSAGE}
          </Alert>
        )}

        {showBreakdownSkeleton ? (
          <BreakdownTableSkeleton />
        ) : (
          <StageBreakdownTable
            panel={breakdownPanel}
            drivers={drivers}
            activeDriver={activeDriver}
            currentPeriodLabel={currentPeriodLabel}
            priorPeriodLabel={priorPeriodLabel}
            comparisonLabel={cmpLabel}
            driverLoading={driverLoading}
            breakdownMetric={breakdownMetricContext}
            currencySymbol={currencySymbol}
            onDriverChange={usingMetricBreakdown ? onMetricDriverChange : onDriverChange}
          />
        )}
      </Box>
    </Paper>
  );
}

export function ChainHealthSummary({ stages }: { stages: L2CStage[] }) {
  const available = stages.filter((s) => !s.data_unavailable);
  const counts = { red: 0, yellow: 0, green: 0 };
  for (const s of available) counts[s.status] += 1;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {(['red', 'yellow', 'green'] as const).map((status) => (
        <Chip
          key={status}
          size="small"
          label={`${counts[status]} ${STATUS_LABELS[status]}`}
          sx={{
            fontWeight: 600,
            bgcolor: alpha(STATUS_COLORS[status], 0.1),
            color: STATUS_COLORS[status],
            border: `1px solid ${alpha(STATUS_COLORS[status], 0.25)}`,
          }}
        />
      ))}
    </Stack>
  );
}
